import React, { ReactElement } from 'react';
import moment from 'moment';
import {
  DatePicker,
  DatePickerDateValue,
  IconText,
  Modal,
} from '@odin-labs/components';
import { CalendarDaysIcon } from 'components/icons';
import { Schedule } from 'containers/schedules/types';
import {
  GetScheduleDetailsDocument,
  GetSchedulesDocument,
  useGetScheduleImportTargetQuery,
  useImportScheduleMutation,
} from 'apollo/generated/client-operations';
import { AlertService } from 'components/alertService';
import { getGraphQLError } from 'utils/error';
import { ImportScheduleModalPreview } from './ImportScheduleModalPreview';

type SelectedSchedule = Pick<Schedule, 'id' | 'date'> & {
  jobsite: Pick<Schedule['jobsite'], 'jobsiteId' | 'name'>;
};

export function ImportScheduleModal(props: {
  isOpen: boolean;
  closeModal: () => void;
  onClosed?: () => void;
  selectedSchedule?: SelectedSchedule;
  refetchQuery: typeof GetSchedulesDocument | typeof GetScheduleDetailsDocument;
}): ReactElement {
  const { closeModal, isOpen, onClosed, selectedSchedule, refetchQuery } =
    props;
  const [date, setDate] = React.useState<DatePickerDateValue>(null);

  const onAfterLeave = React.useCallback((): void => {
    // Reset the modal state
    setDate(null);
    onClosed?.();
  }, [onClosed]);

  const { data, loading: isSearchingForSchedule } =
    useGetScheduleImportTargetQuery({
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      skip: !date || !isOpen || !selectedSchedule,
      variables: {
        input: {
          jobsiteId: selectedSchedule?.jobsite?.jobsiteId,
          date: date?.format('YYYY-MM-DD'),
        },
      },
    });
  const { shifts: sourceShifts, id: sourceId } = data?.getSchedule ?? {};

  const [importSchedule, { loading: isSaving }] = useImportScheduleMutation({
    refetchQueries: [refetchQuery],
  });

  const onAction = React.useCallback(async () => {
    try {
      await importSchedule({
        variables: {
          input: {
            id: selectedSchedule?.id,
            sourceId,
          },
        },
      });
      AlertService.alert(
        'success',
        'Success',
        'Schedule imported successfully',
      );
      closeModal();
    } catch (error) {
      AlertService.alert(
        'danger',
        'Something went wrong!',
        getGraphQLError(error),
      );
    }
  }, [selectedSchedule, closeModal, importSchedule, sourceId]);

  const { date: scheduleDate } = selectedSchedule ?? {};
  const scheduleDateText =
    scheduleDate && moment(scheduleDate).format('MMMM D, YYYY');

  return (
    <Modal
      actionText="Import schedule"
      actionButtonTheme="primary"
      actionButtonEnabled={!!date && !isSearchingForSchedule && !!sourceId}
      actionButtonWithSpinner={isSaving}
      afterLeave={onAfterLeave}
      cancelText="Cancel"
      onAction={onAction}
      open={isOpen}
      setOpen={closeModal}
      size="xs"
      title="Import schedule"
    >
      <div className="odin-flex odin-flex-col odin-gap-8">
        <div className="odin-flex odin-gap-4">
          <IconText icon={CalendarDaysIcon} size="md" text={scheduleDateText} />
        </div>
        <div className="odin-flex odin-flex-col odin-gap-2">
          <p>
            Choose a schedule you would like to import into the current
            schedule.
          </p>
          <DatePicker
            icon={CalendarDaysIcon}
            name="targetDate"
            onChange={(value) => setDate(value)}
            value={date}
          />
        </div>
        {isOpen && (
          <ImportScheduleModalPreview
            date={date}
            isLoading={isSearchingForSchedule}
            isScheduleFound={!!sourceId}
            shiftCount={sourceShifts?.length}
            jobsiteName={selectedSchedule?.jobsite?.name}
          />
        )}
      </div>
    </Modal>
  );
}
